import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Alert, Popover, Stack, Tooltip, Typography } from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import {
  Button,
  IconButton,
  Input,
  SearchableSelect,
} from '../../../../components/ui';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import { parseTemplateListResponse } from '../../../whatsapp-templates/adapters/templateListAdapter';
import { mapBotToSelectOption } from '../../../whatsapp-templates/utils/normalizeTemplate';
import { templateService } from '../../../whatsapp-templates/services/templateService';
import { normalizeWhatsappTemplateOptions } from '../../utils/templateOptions';
import { analyticsService } from '../../services/analyticsService';
import ValueInclusionPicker from '../builder/ValueInclusionPicker';
import {
  CUSTOM_DATE_PRESET,
  REPORT_DEFAULT_PRESET,
  datePresetLabels,
} from '../../utils/datePresets';
import { kolkataToday, addDays } from '../../utils/previousPeriod';

const ALL_TEMPLATE = { id: '', label: ANALYTICS_COPY.whatsappTemplateAll };
const ALL_AGENTS = { id: '', label: ANALYTICS_COPY.interveneAgentFilterPlaceholder };
const ALL_CUSTOMERS = { id: '', label: ANALYTICS_COPY.interveneCustomerFilterPlaceholder };

const DATE_LABELS = datePresetLabels(ANALYTICS_COPY);
const CARD_DATE_OPTIONS = [
  { id: REPORT_DEFAULT_PRESET, label: 'Default (Dashboard date)' },
  { id: 'today', label: DATE_LABELS.today },
  { id: 'yesterday', label: DATE_LABELS.yesterday },
  { id: 'last_7_days', label: DATE_LABELS.last_7_days },
  { id: 'last_30_days', label: DATE_LABELS.last_30_days },
  { id: 'last_90_days', label: DATE_LABELS.last_90_days },
  { id: 'this_month', label: DATE_LABELS.this_month },
  { id: 'this_week', label: DATE_LABELS.this_week },
  { id: 'all_time', label: DATE_LABELS.all_time },
  { id: CUSTOM_DATE_PRESET, label: DATE_LABELS.custom },
];

function toAgentOptions(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.agents)
      ? data.agents
      : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.agents)
          ? data.data.agents
          : [];

  return [
    ALL_AGENTS,
    ...list
      .map((agent) => {
        const username = agent?.username;
        if (!username) return null;
        return {
          id: String(username),
          label: agent?.display_name || agent?.profile_name || username,
          username: String(username),
          userId: agent?.user_id == null ? '' : String(agent.user_id),
        };
      })
      .filter(Boolean),
  ];
}

function toCustomerOptions(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.values)
      ? data.values
      : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.values)
          ? data.data.values
          : [];
  return [
    ALL_CUSTOMERS,
    ...list
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .map((value) => ({ id: value, label: value })),
  ];
}

function syncDraftFromApplied(filters = {}) {
  const tIds = Array.isArray(filters.templateIds) && filters.templateIds.length
    ? filters.templateIds.map(String).filter(Boolean)
    : Array.isArray(filters.template_id)
      ? filters.template_id.map(String).filter(Boolean)
      : Array.isArray(filters.templateId)
        ? filters.templateId.map(String).filter(Boolean)
        : (filters.templateId && filters.templateId !== 'all' ? [String(filters.templateId)] : []);

  const agUsernames = Array.isArray(filters.agents) && filters.agents.length
    ? filters.agents.map(String).filter(Boolean)
    : Array.isArray(filters.agent)
      ? filters.agent.map(String).filter(Boolean)
      : (filters.agent && filters.agent !== 'all' ? [String(filters.agent)] : []);

  const agIds = Array.isArray(filters.agentUserIds) && filters.agentUserIds.length
    ? filters.agentUserIds.map(String).filter(Boolean)
    : Array.isArray(filters.agentUserId)
      ? filters.agentUserId.map(String).filter(Boolean)
      : (filters.agentUserId ? [String(filters.agentUserId)] : []);

  const custPhones = Array.isArray(filters.customers) && filters.customers.length
    ? filters.customers.map(String).filter(Boolean)
    : Array.isArray(filters.customer)
      ? filters.customer.map(String).filter(Boolean)
      : (filters.customer && filters.customer !== 'all' ? [String(filters.customer)] : []);

  const dateRange = filters.dateRange?.preset
    ? { ...filters.dateRange }
    : { preset: REPORT_DEFAULT_PRESET };

  return {
    ...filters,
    dateRange,
    botNumber: filters.botNumber || filters.bot_number || '',
    templateId: tIds[0] || '',
    templateIds: tIds,
    agent: agUsernames[0] || '',
    agents: agUsernames,
    agentUserId: agIds[0] || '',
    agentUserIds: agIds,
    customer: custPhones[0] || '',
    customers: custPhones,
    dimensionValues: Array.isArray(filters.dimensionValues) ? [...filters.dimensionValues] : [],
  };
}

export default function ReportCardFilters({
  dataset,
  orgId,
  appliedFilters = {},
  templates,
  querySpec = {},
  campaignIds = [],
  leadsFormIds = [],
  onApply,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(() => syncDraftFromApplied(appliedFilters));
  const [agentOptions, setAgentOptions] = useState([ALL_AGENTS]);
  const [customerOptions, setCustomerOptions] = useState([ALL_CUSTOMERS]);
  const [loadedTemplateOptions, setLoadedTemplateOptions] = useState([]);
  const [botOptions, setBotOptions] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isWhatsApp = dataset === 'whatsapp_templates';
  const isIntervene = dataset === 'intervene';
  const isRecordsDimension = dataset === 'records' && Boolean(querySpec.dimension);

  useEffect(() => {
    if (!isWhatsApp || !orgId) return;
    let cancelled = false;
    async function loadBots() {
      try {
        const res = await templateService.getBotNumbers(orgId);
        if (cancelled) return;
        const rawList = Array.isArray(res.data) ? res.data : res.data?.list ?? res.data?.results ?? [];
        setBotOptions(rawList.map(mapBotToSelectOption).filter(Boolean));
      } catch {
        // ignore
      }
    }
    loadBots();
    return () => { cancelled = true; };
  }, [isWhatsApp, orgId]);
  const open = Boolean(anchorEl);

  const availableTemplateOptions = useMemo(
    () => normalizeWhatsappTemplateOptions(templates, ALL_TEMPLATE.label),
    [templates]
  );

  const allTemplateOptions = useMemo(() => {
    const map = new Map();
    loadedTemplateOptions.forEach((t) => {
      if (t.id && t.id !== 'all') map.set(String(t.id), t);
    });
    availableTemplateOptions.forEach((t) => {
      if (t.id && t.id !== 'all' && !map.has(String(t.id))) {
        map.set(String(t.id), t);
      }
    });
    (draft.templateIds || []).forEach((id) => {
      if (id && id !== 'all' && !map.has(String(id))) {
        map.set(String(id), { id: String(id), label: String(id) });
      }
    });
    return Array.from(map.values());
  }, [availableTemplateOptions, loadedTemplateOptions, draft.templateIds]);

  const selectedTemplateIds = useMemo(() => {
    if (Array.isArray(draft.templateIds)) return draft.templateIds.map(String).filter(Boolean);
    if (Array.isArray(draft.templateId)) return draft.templateId.map(String).filter(Boolean);
    if (draft.templateId && draft.templateId !== 'all') return [String(draft.templateId)];
    return [];
  }, [draft.templateId, draft.templateIds]);

  const selectedTemplateOptions = useMemo(() => {
    return selectedTemplateIds.map((id) => {
      const found = allTemplateOptions.find((t) => String(t.id) === String(id));
      return found || { id: String(id), label: String(id) };
    });
  }, [allTemplateOptions, selectedTemplateIds]);

  const selectedAgentUsernames = useMemo(() => {
    if (Array.isArray(draft.agents)) return draft.agents.map(String).filter(Boolean);
    if (Array.isArray(draft.agent)) return draft.agent.map(String).filter(Boolean);
    if (draft.agent && draft.agent !== 'all') return [String(draft.agent)];
    return [];
  }, [draft.agent, draft.agents]);

  const selectedAgentOptions = useMemo(() => {
    return selectedAgentUsernames.map((uname) => {
      const found = agentOptions.find((a) => String(a.username || a.id) === String(uname));
      return found || { id: String(uname), label: String(uname), username: String(uname) };
    });
  }, [agentOptions, selectedAgentUsernames]);

  const selectedCustomerPhones = useMemo(() => {
    if (Array.isArray(draft.customers)) return draft.customers.map(String).filter(Boolean);
    if (Array.isArray(draft.customer)) return draft.customer.map(String).filter(Boolean);
    if (draft.customer && draft.customer !== 'all') return [String(draft.customer)];
    return [];
  }, [draft.customer, draft.customers]);

  const selectedCustomerOptions = useMemo(() => {
    return selectedCustomerPhones.map((phone) => {
      const found = customerOptions.find((c) => String(c.id) === String(phone));
      return found || { id: String(phone), label: String(phone) };
    });
  }, [customerOptions, selectedCustomerPhones]);

  const hasDateSelection = Boolean(
    draft.dateRange?.preset && draft.dateRange.preset !== REPORT_DEFAULT_PRESET
  );

  const hasSelection = Boolean(
    hasDateSelection
    || selectedTemplateIds.length
    || draft.botNumber
    || selectedAgentUsernames.length
    // || selectedCustomerPhones.length
    || draft.dimensionValues?.length
  );

  useEffect(() => {
    if (!open) return;
    setDraft(syncDraftFromApplied(appliedFilters));
    setLoadError('');
  }, [open, appliedFilters]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    async function loadOptions() {
      try {
        setIsLoading(true);
        if (isWhatsApp) {
          let list = [];
          try {
            const res = await templateService.getTemplates({
              organisation: orgId,
              page: 0,
              limit: 200,
              archived: 'false',
            });
            const parsed = parseTemplateListResponse(res);
            list = parsed?.templates || res?.data?.data || res?.data || [];
          } catch {
            // fallback to analyticsService.getTemplates
          }
          if (!list || !list.length) {
            try {
              const res2 = await analyticsService.getTemplates();
              list = res2?.data || res2 || [];
            } catch {
              // ignore
            }
          }
          if (cancelled) return;
          const normalized = normalizeWhatsappTemplateOptions(list, '');
          const items = normalized.filter((item) => Boolean(item.id) && item.id !== 'all');
          if (items.length > 0) {
            setLoadedTemplateOptions(items);
          }
        } else if (isIntervene) {
          const agentsRes = await analyticsService.getInterveneAgents(orgId);
          // Customer phone filter commented out per user request
          // const customersRes = await analyticsService.getInterveneFieldValues('phone_number', orgId);
          if (cancelled) return;
          const errors = [];
          if (agentsRes.ok) {
            setAgentOptions(toAgentOptions(agentsRes.data));
          } else {
            errors.push(ANALYTICS_COPY.interveneAgentFilterLabel);
          }
          if (errors.length) {
            setLoadError(ANALYTICS_COPY.cardFiltersLoadFailed);
          }
        }
      } catch {
        if (!cancelled) setLoadError(ANALYTICS_COPY.cardFiltersLoadFailed);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [open, isWhatsApp, isIntervene, orgId]);

  const handleApply = () => {
    onApply?.(draft);
    setAnchorEl(null);
  };

  const handleClear = () => {
    const clearedFilters = {
      dateRange: { preset: REPORT_DEFAULT_PRESET },
      botNumber: '',
      templateId: '',
      templateIds: [],
      agent: '',
      agents: [],
      agentUserId: '',
      agentUserIds: [],
      customer: '',
      customers: [],
      dimensionValues: [],
    };
    setDraft(clearedFilters);
    onApply?.(clearedFilters);
    setAnchorEl(null);
  };

  const handleTemplatesChange = (arg1, arg2) => {
    const raw = arg2 !== undefined ? arg2 : arg1;
    const opts = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const ids = opts
      .map((o) => (typeof o === 'string' ? o : o?.id || o?.value))
      .filter((id) => Boolean(id) && id !== 'all');
    setDraft((current) => ({
      ...current,
      templateId: ids[0] || '',
      templateIds: ids,
    }));
  };

  const handleAgentsChange = (arg1, arg2) => {
    const raw = arg2 !== undefined ? arg2 : arg1;
    const opts = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const filtered = opts.filter((o) => {
      const id = typeof o === 'string' ? o : (o?.username || o?.id);
      return Boolean(id) && id !== 'all';
    });
    const usernames = filtered.map((o) => (typeof o === 'string' ? o : o?.username || o?.id)).filter(Boolean);
    const userIds = filtered.map((o) => (typeof o === 'string' ? '' : o?.userId || o?.user_id)).filter(Boolean);
    setDraft((current) => ({
      ...current,
      agent: usernames[0] || '',
      agents: usernames,
      agentUserId: userIds[0] || '',
      agentUserIds: userIds,
    }));
  };

  const handleCustomersChange = (arg1, arg2) => {
    const raw = arg2 !== undefined ? arg2 : arg1;
    const opts = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const phones = opts
      .map((o) => (typeof o === 'string' ? o : o?.id || o?.label || o?.value))
      .filter((id) => Boolean(id) && id !== 'all');
    setDraft((current) => ({
      ...current,
      customer: phones[0] || '',
      customers: phones,
    }));
  };

  return (
    <>
      <Tooltip title={ANALYTICS_COPY.cardFiltersTitle}>
        <IconButton
          size="sm"
          variant={hasSelection ? 'primary' : 'ghost'}
          aria-label={ANALYTICS_COPY.cardFiltersTitle}
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
        >
          <FilterListOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 340, p: 2 } } }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle2" fontWeight={600}>
            {ANALYTICS_COPY.cardFiltersTitle}
          </Typography>
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}
          <Box>
            <SearchableSelect
              label={ANALYTICS_COPY.builderStep2DateLabel}
              options={CARD_DATE_OPTIONS}
              value={
                CARD_DATE_OPTIONS.find((o) => o.id === (draft.dateRange?.preset || REPORT_DEFAULT_PRESET))
                || CARD_DATE_OPTIONS[0]
              }
              onChange={(opt) => {
                const nextPreset = opt?.id || REPORT_DEFAULT_PRESET;
                if (nextPreset === CUSTOM_DATE_PRESET) {
                  setDraft((curr) => ({
                    ...curr,
                    dateRange: { preset: CUSTOM_DATE_PRESET, start: '', end: '' },
                  }));
                } else if (nextPreset === 'today') {
                  const today = kolkataToday();
                  setDraft((curr) => ({
                    ...curr,
                    dateRange: { preset: 'today', start: today, end: today },
                  }));
                } else if (nextPreset === 'yesterday') {
                  const yDay = addDays(kolkataToday(), -1);
                  setDraft((curr) => ({
                    ...curr,
                    dateRange: { preset: 'yesterday', start: yDay, end: yDay },
                  }));
                } else {
                  setDraft((curr) => ({
                    ...curr,
                    dateRange: { preset: nextPreset },
                  }));
                }
              }}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || String(option?.id || ''))}
              isOptionEqualToValue={(a, b) => String(a?.id ?? a) === String(b?.id ?? b)}
              clearable={false}
            />
            {draft.dateRange?.preset === CUSTOM_DATE_PRESET ? (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Input
                  label={ANALYTICS_COPY.dateRangeLabel}
                  type="date"
                  value={draft.dateRange?.start || ''}
                  inputProps={{ max: draft.dateRange?.end || undefined }}
                  onChange={(e) => {
                    const startVal = e.target.value;
                    setDraft((curr) => ({
                      ...curr,
                      dateRange: { ...curr.dateRange, start: startVal },
                    }));
                  }}
                />
                <Input
                  label={ANALYTICS_COPY.dateRangeEndLabel}
                  type="date"
                  value={draft.dateRange?.end || ''}
                  inputProps={{ min: draft.dateRange?.start || undefined }}
                  onChange={(e) => {
                    const endVal = e.target.value;
                    setDraft((curr) => ({
                      ...curr,
                      dateRange: { ...curr.dateRange, end: endVal },
                    }));
                  }}
                />
              </Stack>
            ) : null}
          </Box>
          {isWhatsApp ? (
            <>
              <SearchableSelect
                label="Bot Number"
                placeholder="All Bot Numbers"
                options={botOptions}
                value={
                  botOptions.find(
                    (b) => String(b.id) === String(draft.botNumber) || String(b.value) === String(draft.botNumber)
                  ) || (draft.botNumber ? { id: String(draft.botNumber), label: String(draft.botNumber) } : null)
                }
                onChange={(opt) => {
                  const nextBot = opt?.id || opt?.value ? String(opt.id || opt.value) : '';
                  setDraft((current) => ({
                    ...current,
                    botNumber: nextBot,
                    templateIds: [],
                    templateId: '',
                  }));
                }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || String(option?.id || ''))}
                isOptionEqualToValue={(a, b) => String(a?.id ?? a?.value) === String(b?.id ?? b?.value)}
                clearable
              />
              <SearchableSelect
                multiple
                label={ANALYTICS_COPY.whatsappTemplateLabel}
                placeholder={selectedTemplateOptions.length ? '' : 'Select template(s)...'}
                options={allTemplateOptions}
                value={selectedTemplateOptions}
                onChange={handleTemplatesChange}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || String(option?.id || ''))}
                isOptionEqualToValue={(a, b) => String(a?.id ?? a) === String(b?.id ?? b)}
                loading={isLoading}
                clearable={false}
                noOptionsText={ANALYTICS_COPY.cardFiltersNoTemplates}
              />
            </>
          ) : null}
          {isIntervene ? (
            <>
              <SearchableSelect
                multiple
                label={ANALYTICS_COPY.interveneAgentFilterLabel}
                placeholder={selectedAgentOptions.length ? '' : 'Select user(s)...'}
                options={agentOptions.filter((a) => a.id && a.id !== 'all')}
                value={selectedAgentOptions}
                onChange={handleAgentsChange}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || String(option?.username || option?.id || ''))}
                isOptionEqualToValue={(a, b) => {
                  const aVal = a?.username || a?.id || a;
                  const bVal = b?.username || b?.id || b;
                  return String(aVal) === String(bVal);
                }}
                loading={isLoading}
                clearable={false}
              />
              {/* Customer phone filter commented out per user request
              <SearchableSelect
                multiple
                label={ANALYTICS_COPY.interveneCustomerFilterLabel}
                placeholder={selectedCustomerOptions.length ? '' : 'Select customer phone(s)...'}
                options={customerOptions.filter((c) => c.id && c.id !== 'all')}
                value={selectedCustomerOptions}
                onChange={handleCustomersChange}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || String(option?.id || ''))}
                isOptionEqualToValue={(a, b) => String(a?.id ?? a?.label ?? a) === String(b?.id ?? b?.label ?? b)}
                loading={isLoading}
                clearable={false}
              />
              */}
            </>
          ) : null}
          {isRecordsDimension ? (
            <ValueInclusionPicker
              fieldId={querySpec.dimension}
              campaignIds={campaignIds}
              leadsFormIds={leadsFormIds}
              selected={draft.dimensionValues || []}
              onChange={(dimensionValues) =>
                setDraft((current) => ({ ...current, dimensionValues }))
              }
            />
          ) : null}
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            {hasSelection ? (
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={handleClear}
              >
                {ANALYTICS_COPY.cardFiltersClear}
              </Button>
            ) : <span />}
            <Stack direction="row" spacing={1}>
              <Button variant="outline" onClick={() => setAnchorEl(null)}>
                {ANALYTICS_COPY.deleteReportCancel}
              </Button>
              <Button variant="primary" disabled={isLoading} onClick={handleApply}>
                {ANALYTICS_COPY.cardFiltersApply}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
